import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { fg } from '@confluence/feature-gating';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useMessageData } from '@atlassian/post-office-headless-api/hooks/useMessageData';
import { PlacementFetchProvider } from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';

import spotlightMediaPlaceholder from './assets/spotlight-media-placeholder.svg';

const MESSAGE_TEMPLATE_ID = 'confluence-native-tabs-toolbar-spotlight';
const PLACEMENT_ID = 'headless';

const messages = defineMessages({
	body: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.body',
		defaultMessage:
			'Structure related content into easy-to-scan tabs, so readers can quickly find what they need.',
		description: 'Body text for the Native Tabs toolbar spotlight.',
	},
	done: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.done',
		defaultMessage: 'Done',
		description: 'Button text that dismisses the Native Tabs toolbar spotlight.',
	},
	title: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.title',
		defaultMessage: 'Organize your content with tabs',
		description: 'Title for the Native Tabs toolbar spotlight.',
	},
});

type TabsButtonSpotlightProps = {
	children: React.ReactElement;
};

const TabsButtonSpotlightMessage = ({ children }: TabsButtonSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { Spotlight, SpotlightTarget } = useChoreographerAssets();
	const { message, PostOfficeMessage, dismissMessage } = useMessageData({
		placementId: PLACEMENT_ID,
		messageTemplateId: MESSAGE_TEMPLATE_ID,
	});

	const handleDismiss = useCallback(() => {
		if (message) {
			dismissMessage(message.messageInstanceId);
		}
	}, [dismissMessage, message]);
	const spotlightActions = useMemo(
		() => [
			{
				text: formatMessage(messages.done),
				onClick: handleDismiss,
			},
		],
		[formatMessage, handleDismiss],
	);

	const spotlight = (
		<React.Fragment>
			<SpotlightTarget name="native-tabs-toolbar-spotlight-target">{children}</SpotlightTarget>
			{!!message && (
				<Spotlight
					actions={spotlightActions}
					dialogPlacement="bottom center"
					dialogWidth={295}
					heading={formatMessage(messages.title)}
					image={spotlightMediaPlaceholder}
					isMigrated
					key="native-tabs-toolbar-spotlight"
					messageId="native-tabs-toolbar-spotlight"
					messageType="engagement"
					target="native-tabs-toolbar-spotlight-target"
					targetBgColor={token('elevation.surface.overlay')}
					targetRadius={3}
				>
					{formatMessage(messages.body)}
				</Spotlight>
			)}
		</React.Fragment>
	);

	if (!PostOfficeMessage || !message) {
		return children;
	}

	return <PostOfficeMessage>{() => spotlight}</PostOfficeMessage>;
};
export const TabsButtonSpotlight = ({ children }: TabsButtonSpotlightProps): React.JSX.Element => {
	if (!fg('post-office_enable_conf-tabs-toolbar-spotlight')) {
		return children;
	}

	return (
		<PlacementFetchProvider>
			<TabsButtonSpotlightMessage>{children}</TabsButtonSpotlightMessage>
		</PlacementFetchProvider>
	);
};
